import { createLocation } from './utils/url'
import { getEle, getElementText, transLogDataset, matchLogElement, getXpathByElement } from './utils/element'
import { concatText, removeSpace } from './utils/text'
import { omit } from './utils/obj'
import debounce from 'lodash/debounce'

import { Config, Location, RelatedInfo } from './interface'

const EVENT_ID = '3001'
const PV_ID = '2001'

// fixed：radio组件点击触发两次
const DEBOUNCE_TIME = 30

class Logger {
    // 配置项
    private _config: Config = {}
    // 路由信息
    private _location: Location[] = []
    // 上下文信息
    private _context: object = {}

    public constructor(config: Config) {
        Object.assign(this, {
            _config: config,
        })

        this._context = this.getContext()
    }

    // 获取当前页面的url和参数
    public getPage() {
        const { ignorePageQueryKeys } = this._config

        const defLocation = [createLocation(document.referrer || ''), createLocation(window.location.href)]
        const [prevLoc, curLoc] = this._location.length ? this._location : defLocation

        const obj = {
            page: curLoc.pathname,
            page_args: curLoc.query,
            refer: prevLoc.pathname,
            refer_args: prevLoc.query,
        }

        return omit(obj, ignorePageQueryKeys, true)
    }

    // 获取上下文
    public getContext() {
        const { context } = this._config

        const obj = typeof context === 'function' ? context() : context

        return {
            ...this._context,
            ...(obj || {}),
        }
    }

    // 设置上下文
    public setContext(data: any) {
        const obj = this.getContext()

        Object.assign(this, {
            _context: {
                ...(obj || {}),
                ...(data || {}),
            },
        })
    }

    // 异常捕获
    public trackError(e: any) {
        const { trackError } = this._config

        if (typeof trackError === 'function') {
            trackError(e)
        }
    }

    // 发送数据
    public sendData(e: any) {
        const { sendData } = this._config

        if (typeof sendData === 'function') {
            sendData(e)
        }
    }

    // 推送路由
    public pushLocation(location?: Location) {
        const { transLocation } = this._config

        const trans = typeof transLocation === 'function' ? transLocation : (loc: any) => loc

        const newLocation = trans(location || createLocation(window.location.href))

        let locationArr = []

        if (this._location[1]?.pathname) {
            locationArr = this._location.slice(1).concat(newLocation)
        } else {
            locationArr = [trans(createLocation(document.referrer || '')), newLocation]
        }

        this._location = locationArr
    }

    // event
    public event(data?: any) {
        const context = this.getContext()

        const page: object = this.getPage()

        const obj = {
            event_id: EVENT_ID,
            ...context,
            ...page,
            ...(data || {}),
        }

        this.sendData(obj)
    }

    // pv
    public pv(data?: any, options = { force: false }) {
        const { ignorePvPathname = [] } = this._config
        const { force } = options || {}

        const [prevLoc, curLoc] = this._location

        // 是否忽略
        const needIgnore = Array.isArray(ignorePvPathname) && ignorePvPathname.includes(curLoc.pathname)
        // 是否重复
        const isRepeat = prevLoc.origin === curLoc.origin && prevLoc.pathname === curLoc.pathname

        if (!force && (needIgnore || isRepeat)) {
            return
        }

        const context = this.getContext()

        const page: object = this.getPage()

        const obj = {
            event_id: PV_ID,
            ...context,
            ...page,
            ...(data || {}),
        }

        this.sendData(obj)
    }

    // 初始化
    public init() {
        const { autoLogEvent } = this._config

        autoLogEvent && this.bindEvents(true)
    }

    // 销毁：解绑事件等
    public destroy() {
        this.bindEvents(false)
    }

    // 事件
    public bindEvents(bind: boolean) {
        const { rootEl } = this._config

        const action = bind ? 'addEventListener' : 'removeEventListener'

        const el = getEle(rootEl)

        const nodeList = (Array.isArray(el) ? el : [el]).filter(Boolean)

        if (nodeList.length) {
            nodeList.forEach(node => {
                node[action]('click', this._clickHandler, true)
            })
        }
    }

    private _clickHandler = debounce(
        (e: Event) => {
            if (!e.target) {
                return
            }

            const { markedRules, resolveRelatedInfo, ignoreAutoEventPathname = [], beforeAutoLogEvent } = this._config

            const [, curLoc] = this._location
            const needIgnore =
                curLoc && Array.isArray(ignoreAutoEventPathname) && ignoreAutoEventPathname.includes(curLoc.pathname)

            // 忽略
            if (needIgnore) {
                return
            }

            try {
                const { transText = concatText } = this._config

                const element = matchLogElement(e.target as HTMLElement, markedRules)

                if (element) {
                    const dataset: any = transLogDataset(element.logData)

                    const parentInfo: any = typeof resolveRelatedInfo === 'function' ? resolveRelatedInfo(element) : []

                    const trimText = removeSpace(dataset.fullText || dataset.text || getElementText(element))
                    const fullText =
                        // eslint-disable-next-line no-nested-ternary
                        typeof transText === 'function'
                            ? dataset.fullText
                                ? trimText
                                : transText(trimText, parentInfo)
                            : trimText

                    // 供应链专属
                    if (typeof beforeAutoLogEvent === 'function') {
                        beforeAutoLogEvent({
                            elemKey: dataset.key || '',
                            elemText: fullText,
                            elemXpath: getXpathByElement(element),
                            elemData: omit(dataset, ['key', 'element', 'text', 'fullText']),
                        })
                    }

                    const obj = {
                        element: dataset.element,
                        element_args: {
                            ...omit(dataset, ['key', 'element', 'text', 'fullText']),
                            text: fullText,
                        },
                    }

                    const elementP = parentInfo.find((v: RelatedInfo) => v.element)

                    this.event({
                        ...obj,
                        element: obj?.element || elementP?.element || 'btn_click',
                    })
                }
            } catch (exception) {
                console.error('catchError', exception)
                this.trackError({
                    element: e.target,
                    elemXpath: e.target ? getXpathByElement(e.target) : '',
                    exception,
                })
            }
        },
        DEBOUNCE_TIME,
        {
            leading: true,
            trailing: false,
        },
    )
}

export default Logger
